import { useMutation } from "lib/apollo/client";
import Icon from "hardcover-ui/components/Icon";
import faThumbTack from "icons/regular/thumbtack.svg";
import faThumbTackSolid from "icons/solid/thumbtack.svg";
import bookmarkPromptMutation, { PinPromptMutationData } from "queries/followed_prompts/pinPrompt";
import unbookmarkPromptMutation, { UnpinPromptMutationData } from "queries/followed_prompts/unpinPrompt";
import Button from "hardcover-ui/components/Button";
import { ButtonThemeProps } from "hardcover-ui/theme/components/button";
import { useIsPromptPinned } from "store/currentUser/currentUserSelector";
import { useDispatch } from "react-redux";
import { pinPrompt, unPinPrompt } from "store/currentUser/currentUserSlice";
import { toast } from "sonner";

type Props = {
  promptId: number;
  size?: ButtonThemeProps["size"];
};

export default function PinPromptButton({
  promptId,
  size = "sm",
}: Props) {
  const pinned = useIsPromptPinned(promptId);
  const dispatch = useDispatch();
  const [bookmark, { loading: bookmarkLoading }] = useMutation<PinPromptMutationData>(
    bookmarkPromptMutation
  );
  const [unbookmark, { loading: unbookmarkLoading }] = useMutation<UnpinPromptMutationData>(
    unbookmarkPromptMutation
  );

  const bookmarkPrompt = async () => {
    await bookmark({
      variables: {
        promptId,
      },
    }).then((result) => {
      if(result.data?.upsertResponse?.followedPrompt?.promptId) {
        dispatch(pinPrompt({ promptId }));
      } else {
        toast.error(
          "We ran into an error. Can you try again?"
        );
      }
    });
  };

  const unbookmarkPrompt = async () => {
    await unbookmark({
      variables: {
        promptId,
      },
    }).then((result) => {
      if(result.data?.deleteResponse?.success) {
        dispatch(unPinPrompt({ promptId }));
      } else {
        toast.error(
          "We ran into an error. Can you try again?"
        );
      }
    });
  };

  const toggleBookmark = (e) => {
    e.preventDefault();
    if (pinned) {
      unbookmarkPrompt();
    } else {
      bookmarkPrompt();
    }
  };

  return (
    <Button
      variant="accent"
      size={size}
      data-tooltip-id="tooltip"
      data-tooltip-content="Show off your answers in your profile"
      onClick={toggleBookmark}
      isLoading={bookmarkLoading || unbookmarkLoading}
    >
      <Icon
        icon={pinned ? faThumbTackSolid : faThumbTack}
        size={size === "sm" ? "sm" : size === "xs" ? "sm" : "lg"}
      />
      <span className="whitespace-nowrap">{pinned ? "Unpin from profile" : "Pin to profile"}</span>
    </Button>
  );
}
